/**
 * JurisdictionSelector - Reusable component for jurisdiction selection
 * 
 * Centralizes jurisdiction selection logic used in:
 * - External Portal workflow (standalone section)
 * - Standard Tax workflow (within Current Tax Summary accordion cards)
 * - Deprecated global section (hidden, backward compat)
 * 
 * IMPORTANT: This is a refactor-only component. No workflow changes.
 * - No UI layout changes
 * - No backend contract changes
 * - Backward compatibility maintained
 * 
 * @version 1.0.0
 * @since Feb 26, 2026
 */

import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
// Feb 2026 — Bug fix (Authority Type role-parity).
// JurisdictionSelector previously declared its own 9-item hardcoded list
// that carried the legacy `school` value (label "School") instead of the
// canonical `school_district` (label "School District"). Pull from the
// single source of truth so Tax Processors / Tax Team Leaders see the
// SAME options as Admins. Legacy `school` records are still rendered
// read-only via getJurisdictionTypeLabel().
import { JURISDICTION_TYPE_OPTIONS as CANONICAL_TYPE_OPTIONS, getJurisdictionTypeOptionsForState, getJurisdictionTypeLabelForState } from '../../lib/jurisdictionTaxonomy';

// County-level jurisdiction types — all treated as top-level equivalents to County.
// - Parish: Louisiana's county equivalent
// - Borough: Alaska's county equivalent ONLY. Pennsylvania (and NJ/CT)
//            boroughs are sub-county MUNICIPALITIES, not county-level
//            entities. State-aware routing happens via the helpers
//            below; do not add 'borough' to a static "always county"
//            list — it would re-introduce the Feb 2026 "Darby Borough"
//            defect (auto-stamping `{property_city} + " Borough"`
//            for PA orders and offering no dropdown of real PA
//            boroughs in the master directory).
// These types go through the direct match path, NOT the sub-jurisdiction selector.
const COUNTY_LEVEL_TYPES = ['county', 'parish', 'borough'];

// Jurisdiction types that require sub-jurisdiction selection
// Parish stays county-level (LA).
// Borough is STATE-AWARE — see `requiresSubJurisdiction(type, state)`
// below: borough = county-level only in Alaska; sub-jurisdiction
// (municipal) everywhere else, including PA, NJ, CT.
// Special District is a sub-jurisdiction (user picks a specific district from the state-wide list)
// `school` is kept for backward-compat with legacy records; new records use `school_district`.
const SUB_JURISDICTION_TYPES = ['city', 'town', 'township', 'village', 'school', 'school_district', 'municipality', 'special_district'];

// States where Borough is a county-equivalent (Alaska only). Everywhere
// else, Borough is a sub-county municipality and MUST be picked from
// the master Jurisdiction Directory dropdown rather than auto-stamped
// from the property's mailing city.
const COUNTY_LEVEL_BOROUGH_STATES = new Set(['AK']);

// States where county hierarchy is not operationally relevant
const PARENT_EXEMPT_STATES = ['NJ', 'RI', 'MA', 'NH', 'ME', 'VT', 'CT'];

// Standard jurisdiction type options — sourced from canonical taxonomy.
const JURISDICTION_TYPE_OPTIONS = CANONICAL_TYPE_OPTIONS;

/**
 * Returns TRUE when Borough should behave as a sub-county municipality
 * (i.e. show the sub-jurisdiction dropdown) for the given property
 * state. Alaska keeps the legacy county-level treatment; everywhere
 * else — including PA, NJ, CT — Borough is a municipality.
 *
 * Pass an empty / unknown state to get the legacy (county-level)
 * behaviour; this preserves the historical default for code paths
 * that don't yet thread state through.
 */
const isMunicipalBoroughState = (propertyState) => {
  const s = (propertyState || '').toUpperCase().trim();
  if (!s) return false;  // unknown state — default to legacy county-level
  return !COUNTY_LEVEL_BOROUGH_STATES.has(s);
};

/**
 * Check if the selected type requires sub-jurisdiction selection.
 *
 * `propertyState` (optional) makes Borough state-aware:
 *   - PA / NJ / CT / etc. → Borough = sub-jurisdiction (dropdown)
 *   - AK                  → Borough = county-level (direct match)
 *   - state unknown       → Borough stays county-level (legacy default)
 *
 * All other types are state-invariant.
 */
const requiresSubJurisdiction = (jurisdictionType, propertyState) => {
  if (!jurisdictionType) return false;
  const t = jurisdictionType.toLowerCase();
  if (t === 'borough') {
    return isMunicipalBoroughState(propertyState);
  }
  return SUB_JURISDICTION_TYPES.includes(t);
};

/**
 * Classify a (type, state) pair into one of:
 *   'county_level' | 'sub_jurisdiction' | 'special_district' | 'other'
 *
 * This is the canonical state-aware classifier used by the
 * Fulfillment surface to decide which lookup branch to take. Always
 * prefer this over a raw `COUNTY_LEVEL_TYPES.includes()` check —
 * the static array still contains 'borough' for backwards-compatible
 * legacy reads but cannot represent the PA / AK split correctly.
 */
const getEffectiveJurisdictionClassification = (jurisdictionType, propertyState) => {
  if (!jurisdictionType) return 'other';
  const t = jurisdictionType.toLowerCase();
  if (t === 'special_district' || t === 'school' || t === 'school_district') {
    // Special districts and schools are sub-jurisdictions everywhere.
    return t === 'special_district' ? 'special_district' : 'sub_jurisdiction';
  }
  if (t === 'borough') {
    return isMunicipalBoroughState(propertyState) ? 'sub_jurisdiction' : 'county_level';
  }
  if (t === 'county' || t === 'parish') return 'county_level';
  if (SUB_JURISDICTION_TYPES.includes(t)) return 'sub_jurisdiction';
  return 'other';
};

/**
 * JurisdictionSelector Component
 * 
 * @param {Object} props
 * @param {string} props.variant - 'native' (HTML select) or 'shadcn' (shadcn Select)
 * @param {string} props.jurisdictionType - Currently selected jurisdiction type
 * @param {string} props.jurisdictionId - Currently selected jurisdiction ID
 * @param {Function} props.onJurisdictionTypeChange - Handler when type is changed
 * @param {Function} props.onSubJurisdictionChange - Handler when sub-jurisdiction is selected
 * @param {Function} props.onClear - Handler when jurisdiction is cleared
 * @param {Array} props.subJurisdictions - List of sub-jurisdictions to choose from
 * @param {boolean} props.showSubJurisdictionSelector - Whether to show sub-jurisdiction dropdown
 * @param {string} props.selectedCountyForSubJurisdiction - County name for sub-jurisdiction context
 * @param {boolean} props.jurisdictionsLoading - Whether jurisdictions are loading
 * @param {boolean} props.subJurisdictionsLoading - Whether sub-jurisdictions are loading
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {boolean} props.readOnly - Whether in read-only mode
 * @param {string} props.idPrefix - Prefix for element IDs (for accessibility)
 * @param {string} props.testIdPrefix - Prefix for data-testid attributes
 * @param {boolean} props.showWarningWhenNoSelection - Show warning when sub-jurisdiction dropdown has options but none selected
 * @param {string} props.noJurisdictionsMessage - Custom message when no jurisdictions found
 */
const JurisdictionSelector = ({
  variant = 'native',
  jurisdictionType = '',
  jurisdictionId = '',
  // Jun 2026 — LA parish fix: property/order state makes the Type
  // dropdown state-aware (LA hides County, non-LA hides Parish) and
  // display-maps legacy LA `county` values to "Parish". Empty = full list.
  propertyState = '',
  // Feb 2026 — Saved-but-unmatched fallback. When the directory API
  // returns zero options but the order already carries a saved
  // jurisdiction (e.g. PA Boroughs missing from the directory), we
  // surface the saved name as a locked synthetic option so the
  // operator doesn't see "-- No jurisdictions found --" with no
  // indication that their previous selection still exists. The
  // parent (JurisdictionManager) passes the persisted name + id from
  // the order's jurisdiction record.
  currentJurisdictionName = '',
  onJurisdictionTypeChange,
  onSubJurisdictionChange,
  onClear,
  subJurisdictions = [],
  showSubJurisdictionSelector = false,
  selectedCountyForSubJurisdiction = null,
  jurisdictionsLoading = false,
  subJurisdictionsLoading = false,
  disabled = false,
  readOnly = false,
  idPrefix = 'jurisdiction',
  testIdPrefix = 'jurisdiction',
  showWarningWhenNoSelection = true, // deprecated — kept for API compat
  noJurisdictionsMessage = 'No matching jurisdictions in the directory.'
}) => {
  
  const isDisabled = disabled || readOnly;
  // Jun 2026 — state-aware type options + legacy display mapping
  const typeOptions = getJurisdictionTypeOptionsForState(propertyState);
  const currentTypeIsHidden = Boolean(
    jurisdictionType && !typeOptions.some((o) => o.value === jurisdictionType)
  );
  const parentLevelNoun = String(propertyState || '').trim().toUpperCase() === 'LA' ? 'Parish' : 'County';

  // Feb 2026 — Detect the "saved but unmatched" case so the dropdown
  // and advisory can render a synthetic option for the persisted
  // selection instead of erasing it visually.
  //
  //   • subJurisdictions empty (directory lookup returned nothing or
  //     was filtered out post-load), AND
  //   • the order ALREADY carries a saved name / id for this row
  //
  // We DO NOT auto-populate `subJurisdictions` with this synthetic
  // entry — we render it inline only — so the round-trip persistence
  // remains the operator's responsibility (Admin must add the row
  // to the directory to fully "heal" the link).
  const hasSavedButUnmatched = Boolean(
    !subJurisdictionsLoading
    && subJurisdictions.length === 0
    && (currentJurisdictionName || jurisdictionId)
  );
  // Show sub-jurisdiction selector when:
  // 1. Type requires sub-jurisdiction (city, township, etc.).
  //    For Borough this is state-aware: PA/NJ/CT → sub-jurisdiction,
  //    AK → county-level (no selector).
  // 2. AND either: explicitly showing selector, OR a jurisdiction is already selected (allow changing)
  // Note: If jurisdictionId exists, user should be able to see and change their selection
  const showSubSelector = jurisdictionType &&
    requiresSubJurisdiction(jurisdictionType, propertyState) &&
    (showSubJurisdictionSelector || jurisdictionId);
  
  /**
   * Handle jurisdiction type change
   */
  const handleTypeChange = (value) => {
    const selectedType = value === '' || value === '__none__' ? null : value;
    
    if (!selectedType) {
      // Clear jurisdiction - call onClear if provided
      if (onClear) {
        onClear();
      }
      return;
    }
    
    // Delegate to parent handler
    if (onJurisdictionTypeChange) {
      onJurisdictionTypeChange(selectedType);
    }
  };
  
  /**
   * Handle sub-jurisdiction selection
   */
  const handleSubJurisdictionChange = (value) => {
    const jurisdictionIdValue = value === '' || value === '__none__' ? null : value;
    
    if (onSubJurisdictionChange) {
      onSubJurisdictionChange(jurisdictionIdValue);
    }
  };
  
  /**
   * Render native HTML select (used in accordion cards)
   */
  const renderNativeSelect = () => (
    <>
      {/* Jurisdiction Type Dropdown */}
      <div className="md:col-span-2">
        <Label 
          htmlFor={`${idPrefix}_type`} 
          className="text-sm font-medium text-gray-700 mb-1"
        >
          Jurisdiction Type <span className="text-red-500">*</span>
        </Label>
        <select
          id={`${idPrefix}_type`}
          data-testid={`${testIdPrefix}-type-selector`}
          value={jurisdictionType || ''}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
          disabled={isDisabled || jurisdictionsLoading}
        >
          <option value="">-- Select Jurisdiction Type --</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {/* Display-mapped legacy value (e.g. LA record stored as
              `county` shows "Parish"); not re-selectable once changed. */}
          {currentTypeIsHidden && (
            <option value={jurisdictionType} disabled>
              {getJurisdictionTypeLabelForState(jurisdictionType, propertyState)}
            </option>
          )}
        </select>
      </div>
      
      {/* Sub-Jurisdiction Selector */}
      {showSubSelector && (
        <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300">
          <Label 
            htmlFor={`${idPrefix}_name`} 
            className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1"
          >
            <span>Jurisdiction Name</span>
            <span className="text-red-500">*</span>
            {selectedCountyForSubJurisdiction && (
              <span className="text-xs text-gray-500 ml-2">
                (Under {selectedCountyForSubJurisdiction} {parentLevelNoun})
              </span>
            )}
          </Label>
          <select
            id={`${idPrefix}_name`}
            data-testid={`${testIdPrefix}-name-selector`}
            value={jurisdictionId || (hasSavedButUnmatched ? '__saved_unmatched__' : '')}
            onChange={(e) => handleSubJurisdictionChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            disabled={isDisabled || subJurisdictionsLoading}
          >
            <option value="">
              {subJurisdictionsLoading ? 'Loading jurisdictions...' :
               hasSavedButUnmatched ? '-- Select a different Jurisdiction --' :
               subJurisdictions.length === 0 ? '-- No jurisdictions found --' :
               '-- Select Jurisdiction Name --'}
            </option>
            {/* Feb 2026 — Saved-but-unmatched synthetic option. Renders
                the persisted selection so processors don't see "No
                jurisdictions found" for a name that IS saved on the
                order. The option is selectable (its value matches the
                stored jurisdictionId or the synthetic sentinel) and
                shows a clarifying tag so it's visually distinct from
                directory entries. */}
            {hasSavedButUnmatched && (
              <option
                value={jurisdictionId || '__saved_unmatched__'}
                disabled
                data-testid={`${testIdPrefix}-name-saved-unmatched-option`}
              >
                {currentJurisdictionName || '(Saved jurisdiction)'} — saved value, not in directory (re-select)
              </option>
            )}
            {subJurisdictions.map((j) => (
              <option key={j.id} value={j.id}>
                {j.jurisdiction_name || j.name}
                {j.jurisdiction_type ? ` (${j.jurisdiction_type.charAt(0).toUpperCase() + j.jurisdiction_type.slice(1).replace('_', ' ')}` : ''}
                {j.state ? `, ${j.state.toUpperCase()})` : j.jurisdiction_type ? ')' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {subJurisdictionsLoading ? 'Loading available jurisdictions...' :
             hasSavedButUnmatched ? (
               <span
                 className="text-amber-700"
                 data-testid={`${testIdPrefix}-name-saved-unmatched-advisory`}
               >
                 <strong>{currentJurisdictionName || 'This jurisdiction'}</strong> is saved on this order but no matching{' '}
                 {jurisdictionType ? `${jurisdictionType.replace('_', ' ')} ` : ''}
                 record was found in the directory
                 {selectedCountyForSubJurisdiction ? ` under ${selectedCountyForSubJurisdiction} ${parentLevelNoun}` : ''}
                 {propertyState ? ` for ${propertyState.toUpperCase()}` : ''}.
                 Pick a replacement from the list above.
               </span>
             ) : subJurisdictions.length === 0 ? (
               <span className="text-amber-600">{noJurisdictionsMessage}</span>
             ) : (
               `${subJurisdictions.length} jurisdiction(s) available.`
             )}
          </p>
        </div>
      )}
    </>
  );
  
  /**
   * Render shadcn Select (used in some sections)
   */
  const renderShadcnSelect = () => (
    <>
      {/* Jurisdiction Type Dropdown */}
      <div className="md:col-span-2">
        <Label htmlFor={`${idPrefix}_type`}>Jurisdiction Type</Label>
        <Select
          value={jurisdictionType || '__none__'}
          onValueChange={handleTypeChange}
          disabled={isDisabled || jurisdictionsLoading}
        >
          <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-type-selector`}>
            <SelectValue placeholder={jurisdictionsLoading ? "Loading..." : "Select jurisdiction type"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">-- Select Type --</SelectItem>
            {typeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
            {/* Display-mapped legacy value (e.g. LA record stored as
                `county` shows "Parish"); not re-selectable once changed. */}
            {currentTypeIsHidden && (
              <SelectItem value={jurisdictionType} disabled>
                {getJurisdictionTypeLabelForState(jurisdictionType, propertyState)}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">
          System will auto-match based on property location and selected type
        </p>
      </div>
      
      {/* Sub-Jurisdiction Selector */}
      {showSubSelector && (
        <div className="md:col-span-2">
          <Label htmlFor={`${idPrefix}_name`} className="flex items-center gap-1">
            <span>Jurisdiction Name</span>
            <span className="text-red-500">*</span>
            {selectedCountyForSubJurisdiction && (
              <span className="text-xs text-gray-500 ml-2">
                (Under {selectedCountyForSubJurisdiction} {parentLevelNoun})
              </span>
            )}
          </Label>
          <Select
            value={jurisdictionId || (hasSavedButUnmatched ? '__saved_unmatched__' : '__none__')}
            onValueChange={handleSubJurisdictionChange}
            disabled={isDisabled || subJurisdictionsLoading}
          >
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-name-selector`}>
              <SelectValue placeholder={
                subJurisdictionsLoading ? "Loading jurisdictions..." :
                hasSavedButUnmatched ? "Select a different Jurisdiction" :
                subJurisdictions.length === 0 ? "No jurisdictions found" :
                "Select jurisdiction name"
              } />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">-- Select Jurisdiction --</SelectItem>
              {/* Feb 2026 — Saved-but-unmatched synthetic option (shadcn). */}
              {hasSavedButUnmatched && (
                <SelectItem
                  value={jurisdictionId || '__saved_unmatched__'}
                  disabled
                  data-testid={`${testIdPrefix}-name-saved-unmatched-option`}
                >
                  {currentJurisdictionName || '(Saved jurisdiction)'} — saved value, not in directory (re-select)
                </SelectItem>
              )}
              {subJurisdictions.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.jurisdiction_name || j.name}
                  {j.jurisdiction_type ? ` (${j.jurisdiction_type.charAt(0).toUpperCase() + j.jurisdiction_type.slice(1).replace('_', ' ')}` : ''}
                  {j.state ? `, ${j.state.toUpperCase()})` : j.jurisdiction_type ? ')' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            {subJurisdictionsLoading ? 'Loading available jurisdictions...' :
             hasSavedButUnmatched ? (
               <span
                 className="text-amber-700"
                 data-testid={`${testIdPrefix}-name-saved-unmatched-advisory`}
               >
                 <strong>{currentJurisdictionName || 'This jurisdiction'}</strong> is saved on this order but no matching{' '}
                 {jurisdictionType ? `${jurisdictionType.replace('_', ' ')} ` : ''}
                 record was found in the directory
                 {selectedCountyForSubJurisdiction ? ` under ${selectedCountyForSubJurisdiction} ${parentLevelNoun}` : ''}
                 {propertyState ? ` for ${propertyState.toUpperCase()}` : ''}.
                 Pick a replacement from the list above.
               </span>
             ) : subJurisdictions.length === 0 ? noJurisdictionsMessage :
             `${subJurisdictions.length} jurisdiction(s) available. Select one to populate remittance details.`}
          </p>
        </div>
      )}
    </>
  );
  
  // Render based on variant
  return variant === 'shadcn' ? renderShadcnSelect() : renderNativeSelect();
};

// Export constants for use in parent components
export { SUB_JURISDICTION_TYPES, JURISDICTION_TYPE_OPTIONS, PARENT_EXEMPT_STATES, COUNTY_LEVEL_TYPES, COUNTY_LEVEL_BOROUGH_STATES, requiresSubJurisdiction, isMunicipalBoroughState, getEffectiveJurisdictionClassification };

export default JurisdictionSelector;
